import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const kineticWordRevealSkill: Skill = {
    name: SKILL_NAMES.kineticWordReveal,
    kind: 'codeo',
    description:
        'Assemble a hero line word-by-word on the spoken onsets with a decaying rise — the ' +
        'premium kinetic-type treatment for the one line that carries a moment. Max one per scene.',
    instructions: `# Kinetic Word Reveal

The hero line assembles with the read: each word rises on its own spoken onset, and the rise distance *decays* across the line (first word travels far, last word barely moves) so the line lands rather than bouncing uniformly.

Use it on the line that carries the moment — the headline, the verdict, the punchline. Applied to every text block it reads as gimmicky; surrounding text should enter with plain fades so this line stands out.

## Markup

Split the line into word spans. Spans must be \`inline-block\` for transforms to apply; keep real spaces between them so the line wraps and spaces naturally.

\`\`\`html
<h1 id="hero-line" style="position: absolute; left: 160px; top: 440px; margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">
  <span class="kw">Build</span> <span class="kw">videos</span> <span class="kw">faster</span>
</h1>
\`\`\`

\`\`\`css
#hero-line .kw { display: inline-block; }
\`\`\`

## Timeline code

Take each word's onset from your context's narration, which lists words as \`[NN%]\`-of-scene cues. The timeline is authored to the scene's own length, so a percentage converts straight to seconds: \`onset = pct/100 * SCENE_SECONDS\`. Start each rise slightly before its onset (the eye is slower than the ear) and decay the rise distance across the line.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE_SECONDS = 6.4;           // this scene's duration
const onsetPct = [8, 22, 41];        // the [NN%] narration cues
const rises = [80, 44, 12];          // decaying rise across the line
document.querySelectorAll('#hero-line .kw').forEach((el, i) => {
    tl.fromTo(el,
        { opacity: 0, y: rises[i] },
        { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' },
        (onsetPct[i] / 100) * SCENE_SECONDS);
});
tl.set({}, {}, SCENE_SECONDS);       // the line holds for the rest of the scene
window.__timelines = { main: tl };
\`\`\`

## Adapt

- Decay shape: roughly halve the rise per word (\`80 → 44 → 12\`); for longer lines interpolate from ~80 down to ~12 across the words.
- For a heavier landing on the LAST word (it's often the payload), give it \`ease: 'back.out(1.4)'\` while the others keep \`power3.out\`.
- No narration (text-only scene): fabricate onsets ~0.12–0.18s apart — the decay still does its work.

## Pitfalls

- One kinetic line per scene, max. Everything else fades.
- \`display: inline-block\` on the spans is required — transforms don't apply to inline elements.
- The spans are GSAP-tweened on \`y\` — no \`transform\` in their CSS (a CSS transform on the same channel makes the word jump).
- Each \`fromTo\` has a hidden from-state (\`opacity: 0\`), so default \`immediateRender\` correctly keeps later words hidden until their tween — don't add \`immediateRender: false\`, and don't add CSS \`opacity: 0\` on top.
- At ~104px on a 1080-tall frame the line is a true hero; never let a headline fall near the 2.5%-of-height readable floor.`,
};
